import { IntakeFormData } from "../types/domains/intake-form";

export const initialIntakeFormData: IntakeFormData = {
  personalInfo: {
    fullName: "",
    date: new Date().toISOString().split("T")[0],
    phone: "",
    email: "",
    address: "",
    maritalStatus: "",
    jobStatus: "",
    height: "",
    weight: "",
    gender: "",
    dob: "",
    ethnicity: "",
    birthPlace: "",
    emergencyContactName: "",
    emergencyContactRelationship: "",
    emergencyContactPhone: "",
  },
  practitionerQuestions: {
    seenFunctionalHealthPro: "",
    functionalHealthProDetails: "",
    seenFamilyDoctor: "",
    familyDoctorDetails: "",
  },
  healthConcerns: {
    mainConcern: "",
    problemFirstBegin: "",
    lifeAtTheTime: "",
    worseBetter: "",
    healthGoals: "",
    biggestObstacle: "",
    commitmentScale: 5,
    takesMedsForConcern: "",
  },
  preExistingConditions: {
    hasConditions: "",
    conditionsDetails: "",
    hasAllergies: "",
    allergiesDetails: "",
    hasSensitivities: "",
    sensitivitiesDetails: "",
    testedForSensitivities: "",
  },
  familyMedicalHistory: {
    children: "",
    lifeChanges: "",
    losses: "",
    stressors: {
      family: 0,
      work: 0,
      health: 0,
      finances: 0,
      social: 0,
      other: "",
    },
    familyHistory: [{ member: "", age: "", status: "" }],
    hospitalized: "",
    surgeries: "",
    vaccinations: "",
    birthHistory: "",
    wasBreastFed: "",
    childHealth: "",
    antibioticsChildAdult: "",
  },
  lifestyleProfile: {
    education: "",
    occupation: "",
    schedule: "",
    enjoyWork: "",
    satisfiedCompensation: "",
    liveWith: {
      myself: false,
      partner: false,
      roommates: false,
      parents: false,
      children: false,
      other: "",
    },
    moldExposure: "",
  },
  sleepProfile: {
    bedWakeTime: "",
    wakeInNight: "",
    wakeFeelingRested: "",
    mouthBreathe: "",
    sleepAids: "",
    sleepRetainer: "",
    sleepDisruptions: "",
  },
  dentalProfile: {
    mercuryFillings: false,
    rootCanals: false,
    ulcersLesions: false,
    braces: false,
    bruxism: false,
    toothPain: false,
    bleedingGums: false,
    problemsChewing: false,
    tongueTies: false,
    tmj: false,
    gingivitis: false,
    compositeFillings: false,
    dryMouth: false,
    wisdomTeethPulled: false,
    badBreath: false,
  },
  toxinScreen: {
    nonStickPans: "",
    microwave: "",
    antiPerspirant: "",
    candles: "",
    detergent: "",
    fakeNails: "",
    margarine: "",
    nonDairyMilks: "",
    aspartame: "",
    regularSkinCare: "",
    bubbleBath: "",
    regularTampons: "",
    perfume: "",
    processedFoods: "",
    fastFoods: "",
    scentedLotion: "",
    selfTanner: "",
    tapWater: "",
  },
  physicalActivity: {
    activityTable: [
      { activity: "", daysPerWeek: "", timeOfDay: "", duration: "" },
    ],
    furtherNotes: "",
    activityLevel: "",
    fatigueAfterExercise: "",
  },
  dietaryProfile: {
    foodsToNotEat: "",
    favoriteFoods: "",
    perfectDayOfFood: "",
    symptomsIfMealMissed: "",
    symptomsAfterMeals: "",
    foodsAvoided: "",
    specialDiet: "",
    groceryShop: "",
    whoGroceryShops: "",
    mealPlan: "",
    cook: "",
    enjoyCooking: "",
    readLabels: "",
    countCalories: "",
    appetiteChange: "",
    mealsOutPerWeek: "",
    healthEatingChanges: "",
    healthEatingChangesDetails: "",
    servings: {},
    dietHistoryDescription: "",
    pastNutritionalProgram: "",
    pastNutritionalProgramDetails: "",
    pastProgramResults: "",
    pastProgramChallenges: "",
  },
  supplementsMedications: {
    supplements: [
      { typeBrand: "", dosage: "", frequency: "", reason: "", startDate: "" },
    ],
    prescriptions: [
      {
        medication: "",
        dosage: "",
        frequency: "",
        reason: "",
        startEndDate: "",
      },
    ],
    sideEffects: "",
    sideEffectsDetails: "",
    nsaidsUse: "",
    tylenolUse: "",
    acidBlockersUse: "",
    antibiotics3xYear: "",
    longTermAntibiotics: "",
    steroidsUse: "",
    otherMedication: "",
    otherMedicationDetails: "",
    recreationalDrugs: "",
    recreationalDrugsDetails: "",
    marijuanaCBD: "",
    marijuanaCBDDetails: "",
    smoke: "",
    smokeDetails: "",
  },
  psychosocialProfile: {
    lessVital: false,
    likeYourself: false,
    confident: false,
    stressReducesQuality: false,
    meaningAndPurpose: false,
    likeWork: false,
    timeMoneyResponsibilities: false,
    difficultToTrust: false,
    overwhelmed: false,
    practiceMeditation: false,
    abusedOrTrauma: false,
    relaxationTechniques: {},
    stressfulEvents: "",
    worryMost: "",
    relieveStress: "",
  },
  symptomAssessment: {},
  healthAndMedicalConditions: {
    otherConditions: "",
  },
  clientStatement: {
    clientNameStatement: "",
    signature: "",
    dateSigned: "",
  },
};

export const generateMockIntakeFormData = (): IntakeFormData => ({
  personalInfo: {
    fullName: "Jane Doe",
    date: "2024-07-30",
    phone: "555-123-4567",
    email: `test.${Date.now()}@example.com`,
    address: "123 Wellness St, Healthville, USA",
    maritalStatus: "married",
    jobStatus: "working",
    height: "165cm",
    weight: "60kg",
    gender: "female",
    dob: "1990-01-15",
    ethnicity: "Hispanic",
    birthPlace: "Healthville",
    emergencyContactName: "John Doe",
    emergencyContactRelationship: "Husband",
    emergencyContactPhone: "555-765-4321",
  },
  practitionerQuestions: {
    seenFunctionalHealthPro: "yes",
    functionalHealthProDetails:
      "Naturopath, 2 years ago. Helped identify some food sensitivities.",
    seenFamilyDoctor: "yes",
    familyDoctorDetails:
      "Dr. Smith for annual check-ups. Mentioned digestive issues, was told it's likely IBS.",
  },
  healthConcerns: {
    mainConcern:
      "Chronic bloating, gas, and fatigue. Seems to be worse after eating certain foods. It's been ongoing for over a year.",
    problemFirstBegin: "About 1.5 years ago, it became more noticeable.",
    lifeAtTheTime: "Had my second child. Very stressful period.",
    worseBetter:
      "Worse: gluten, dairy, high-sugar foods. Better: when I eat simple, home-cooked meals.",
    healthGoals:
      "Identify root cause of digestive issues, improve energy, and establish a sustainable healthy eating pattern.",
    biggestObstacle:
      "Time and energy to cook healthy meals with two young kids. Stress eating in the evenings.",
    commitmentScale: 9,
    takesMedsForConcern: "yes",
  },
  preExistingConditions: {
    hasConditions: "yes",
    conditionsDetails:
      "Hypothyroidism, diagnosed 5 years ago. Managed with medication.",
    hasAllergies: "no",
    allergiesDetails: "",
    hasSensitivities: "yes",
    sensitivitiesDetails:
      "Gluten, dairy, and soy seem to cause bloating and skin breakouts.",
    testedForSensitivities:
      "Only an elimination diet I did on my own. No formal testing.",
  },
  familyMedicalHistory: {
    children: "2 children, ages 1 and 4.",
    lifeChanges: "Returned to work after maternity leave.",
    losses: "None recently.",
    stressors: {
      family: 8,
      work: 6,
      health: 7,
      finances: 5,
      social: 4,
      other: "Juggling it all",
    },
    familyHistory: [
      { member: "Mother", age: "62", status: "Hashimoto's, Arthritis" },
      { member: "Father", age: "65", status: "High Cholesterol" },
    ],
    hospitalized: "Childbirth x2.",
    surgeries: "None.",
    vaccinations: "Up to date.",
    birthHistory: "C-section for first child, VBAC for second.",
    wasBreastFed: "yes",
    childHealth: "Frequent colds as a child.",
    antibioticsChildAdult:
      "Multiple rounds for ear infections as a child. One round last year for a sinus infection.",
  },
  lifestyleProfile: {
    education: "Bachelor's Degree",
    occupation: "Graphic Designer",
    schedule: "Part-time, flexible hours",
    enjoyWork: "yes",
    satisfiedCompensation: "yes",
    liveWith: {
      myself: false,
      partner: true,
      roommates: false,
      parents: false,
      children: true,
      other: "",
    },
    moldExposure:
      "Lived in an apartment with a leaky bathroom ceiling about 5 years ago.",
  },
  sleepProfile: {
    bedWakeTime: "10:30 PM - 6:30 AM",
    wakeInNight: "Yes, 1-2 times to tend to children.",
    wakeFeelingRested: "no",
    mouthBreathe: "no",
    sleepAids: "Melatonin occasionally.",
    sleepRetainer: "No.",
    sleepDisruptions: "Children waking up, mind racing.",
  },
  dentalProfile: {
    mercuryFillings: true,
    rootCanals: false,
    ulcersLesions: false,
    braces: true,
    bruxism: true,
    toothPain: false,
    bleedingGums: true,
    problemsChewing: false,
    tongueTies: false,
    tmj: true,
    gingivitis: false,
    compositeFillings: true,
    dryMouth: true,
    wisdomTeethPulled: true,
    badBreath: false,
  },
  toxinScreen: {
    nonStickPans: "1",
    microwave: "2",
    antiPerspirant: "3",
    candles: "2",
    detergent: "1",
    fakeNails: "0",
    margarine: "0",
    nonDairyMilks: "3",
    aspartame: "0",
    regularSkinCare: "2",
    bubbleBath: "1",
    regularTampons: "3",
    perfume: "1",
    processedFoods: "2",
    fastFoods: "1",
    scentedLotion: "2",
    selfTanner: "0",
    tapWater: "0",
  },
  physicalActivity: {
    activityTable: [
      {
        activity: "Yoga",
        daysPerWeek: "2",
        timeOfDay: "Evening",
        duration: "45 min",
      },
      {
        activity: "Walking",
        daysPerWeek: "3",
        timeOfDay: "Afternoon",
        duration: "30 min",
      },
    ],
    furtherNotes: "I want to do more but find it hard to find the energy.",
    activityLevel: "moderately",
    fatigueAfterExercise: "No, generally feel better after.",
  },
  dietaryProfile: {
    foodsToNotEat: "Pork.",
    favoriteFoods: "Sushi, dark chocolate, sweet potatoes.",
    perfectDayOfFood:
      "Green smoothie for breakfast, large salad with chicken for lunch, salmon with roasted vegetables for dinner, and a square of dark chocolate.",
    symptomsIfMealMissed: "Irritable, light-headed.",
    symptomsAfterMeals: "Bloating, gas, sometimes brain fog.",
    foodsAvoided: "Bread, cheese, milk.",
    specialDiet: "Trying to be gluten-free and dairy-free.",
    groceryShop: "yes",
    whoGroceryShops: "",
    mealPlan: "yes",
    cook: "yes",
    enjoyCooking: "I enjoy it but it feels like a chore lately.",
    readLabels: "yes",
    countCalories: "no",
    appetiteChange: "Yes, varies with my cycle.",
    mealsOutPerWeek: "1",
    healthEatingChanges: "yes",
    healthEatingChangesDetails: "Trying to cut out processed foods.",
    servings: {
      "Filtered Water": "8 glasses",
      Coffee: "1 cup",
      "Herbal Tea": "2 cups",
      "Nondairy Milk": "1 cup in smoothie",
      Smoothies: "3 per week",
      Alcohol: "1-2 glasses of wine per week",
    },
    dietHistoryDescription: "4",
    pastNutritionalProgram: "no",
    pastNutritionalProgramDetails: "",
    pastProgramResults: "",
    pastProgramChallenges: "",
  },
  supplementsMedications: {
    supplements: [
      {
        typeBrand: "Pure Encapsulations - Vitamin D3",
        dosage: "2000 IU",
        frequency: "Daily",
        reason: "Deficiency",
        startDate: "2022-01-01",
      },
      {
        typeBrand: "Seed - Probiotic",
        dosage: "2 capsules",
        frequency: "Daily",
        reason: "Digestive Health",
        startDate: "2023-06-01",
      },
    ],
    prescriptions: [
      {
        medication: "Levothyroxine",
        dosage: "50mcg",
        frequency: "Daily",
        reason: "Hypothyroidism",
        startEndDate: "2019-present",
      },
    ],
    sideEffects: "yes",
    sideEffectsDetails:
      "Levothyroxine sometimes makes me feel a bit jittery if I have too much coffee.",
    nsaidsUse: "no",
    tylenolUse: "yes",
    acidBlockersUse: "no",
    antibiotics3xYear: "no",
    longTermAntibiotics: "no",
    steroidsUse: "no",
    otherMedication: "no",
    otherMedicationDetails: "",
    recreationalDrugs: "no",
    recreationalDrugsDetails: "",
    marijuanaCBD: "yes",
    marijuanaCBDDetails:
      "Use CBD oil (20mg) occasionally for sleep, maybe 1-2 times a week.",
    smoke: "no",
    smokeDetails: "",
  },
  psychosocialProfile: {
    lessVital: true,
    likeYourself: true,
    confident: true,
    stressReducesQuality: true,
    meaningAndPurpose: true,
    likeWork: true,
    timeMoneyResponsibilities: true,
    difficultToTrust: false,
    overwhelmed: true,
    practiceMeditation: true,
    abusedOrTrauma: false,
    relaxationTechniques: {
      deepbreathing: true,
      yoga: true,
      massage: false,
      coregulation: false,
      meditation: true,
      imagery: false,
      tapping: false,
      grounding: true,
      taichi: false,
      prayer: false,
      "5senses": false,
      physicalactivity: true,
    },
    stressfulEvents: "Juggling work and family life.",
    worryMost: "My children's health and my own health.",
    relieveStress: "Yoga, walking, talking with my partner.",
  },
  symptomAssessment: {
    adrenalHealth: {
      fatigueAfternoon: "2",
      troubleStayingAsleep: "2",
      troubleStayingAsleep_note: "Wake up around 3am",
      cravingsSalty: "1",
      dizzinessStanding: "1",
      slowStartMornings: "2",
      eyesSensitiveBright: "1",
      grindingTeeth: "2",
      difficultyFallingAsleep: "1",
      hives: "0",
      perspireNoActivity: "1",
      wakeTired: "3",
      wakeTired_note: "Almost every day, even with 8 hours of sleep.",
      weakNails: "1",
      afternoonHeadaches: "1",
      troubleRecoveringStress: "2",
      headacheExertion: "0",
      underHighStress: "3",
    },
    vitaminMineral: {
      lossMuscleTone: "1",
      smallBumpsArms: "0",
      racingHeart: "1",
      numbnessTingling: "1",
      numbnessTingling_note: "Sometimes in my feet.",
      feelingDepressed: "1",
      gumsBleedEasily: "2",
      gumsBleedEasily_note: "When brushing teeth.",
      bodyJerksFallingAsleep: "1",
      persistentWorryAnxiety: "2",
      bruiseEasily: "1",
      nosebleeds: "0",
    },
    colonHealth: {
      bowelsNotEmtpy: "2",
      diarrhea: "1",
      ibsColitis: "1",
      historyParasites: "0",
      yeastInfections: "1",
      yeastInfections_note: "Had one last year.",
      itchyAnus: "0",
      itchySkin: "1",
      nailFungus: "0",
      fuzzyTongue: "1",
      looseStools: "1",
      lackDailyMovements: "1",
      abdominalCramping: "2",
      useLaxatives: "0",
      passBadSmellingGas: "2",
      abdominalPainRelieved: "1",
      bloodInStool: "0",
      moreThan3Movements: "0",
      alternatingDiarrheaConstipation: "2",
      hemorrhoids: "1",
      celiacDisease: "0",
      bloatingGrains: "3",
      bloatingGrains_note: "Especially wheat.",
      belching: "2",
      heartburn: "1",
      nauseaVomiting: "1",
      diverticulitis: "0",
    },
    thyroidHealth: {
      nightSweats: "1",
      flushEasily: "1",
      difficultyGainingWeight: "0",
      easilyFatigued: "3",
      easilyFatigued_note: "This is a major concern.",
      coldHandsFeet: "3",
      lowBodyTemp: "1",
      sensitivityCold: "2",
      intolerantHeat: "1",
      heartPalpitations: "1",
      insomnia: "1",
      fastRestingPulse: "0",
      thinningHair: "2",
      thinningEyebrows: "2",
      morningHeadaches: "1",
      stubbornWeightGain: "2",
      lackMotivation: "2",
      nervousness: "1",
      excessivelySleeping: "0",
      drySkin: "3",
      puffyFace: "2",
      weaknessAches: "1",
    },
    respiratoryHealth: {
      asthma: "0",
      chronicSinusitis: "1",
      chronicSinusitis_note: "Gets worse with seasonal allergies.",
      exerciseInducedAsthma: "0",
      sleepApnea: "0",
      pneumonia: "0",
      emphysema: "0",
      bronchitis: "1",
      bronchitis_note: "Had it once a few years ago.",
    },
    liverGallbladder: {
      insomnia: "1",
      stomachUpsetGreasy: "2",
      metallicTaste: "0",
      eyesYellow: "0",
      excessiveHairLoss: "1",
      sensitivityPerfume: "2",
      sensitivityChemicals: "2",
      easilyIntoxicated: "1",
      painRibCage: "1",
      fishTastingBurps: "0",
      palmsRed: "0",
      gallstonesAttack: "0",
      weightGain: "1",
      nausea: "1",
      motionSickness: "1",
      gasBloatingHours: "2",
      unexplainedSwelling: "1",
      painShoulderBlades: "1",
      headacheOverEyes: "1",
      stoolGrey: "0",
      hemorrhoids: "1",
      alcoholAbuse: "0",
      itchyPeelingFeet: "0",
      chronicFatigue: "2",
      darkUrine: "0",
    },
    liverGallbladderConditions: {
      hepatitis: false,
      cirrhosis: false,
      wilsonsDisease: false,
      aids: false,
      gallstones: false,
      nonalcoholicFattyLiver: true,
    },
    endocrineBloodSugar: {
      craveSweets: "3",
      craveSweets_note: "Especially after meals and in the evening.",
      difficultyLosingWeight: "2",
      needSweetsAfterMeal: "3",
      waistGirth: "1",
      increasedThirst: "1",
      frequentUrination: "1",
      relyCoffeeSugar: "2",
      eatingRelievesFatigue: "2",
      craveSugarAfterEating: "2",
      blurredVision: "0",
      difficultyMemory: "1",
      feelingFoggy: "2",
      feelShaky: "2",
      easilyAgitated: "2",
      getLightHeaded: "1",
      getHeadache: "1",
      suddenWeakness: "1",
      nightHunger: "0",
      experienceHungerAfterEating: "1",
      sleepyAfternoon: "3",
      bingeUncontrolled: "1",
      wakeUpTrouble: "1",
    },
    endocrineBloodSugarConditions: {
      hypothyroidism: true,
      hyperthyroidism: false,
      addisonsDisease: false,
      hypoglycemia: true,
      metabolicSyndrome: false,
      chronicFatigueSyndrome: true,
      type1Diabetes: false,
      type2Diabetes: false,
      cushingsSyndrome: false,
    },
    neurologicalHealth: {
      concentrationMemory: "2",
      migraines: "1",
      irritability: "2",
      depression: "1",
      anxietyPanic: "2",
      worry: "3",
      moodSwings: "2",
      vividDreams: "1",
      attentionProblems: "1",
      nightmares: "0",
    },
    neurologicalHealthConditions: {
      addAdhd: false,
      clinicalDepression: false,
      bipolarDisorder: false,
      schizophrenia: false,
      anxietyDisorder: true,
      traumaticBrainInjury: false,
    },
    stomachHealth: {
      diarrheaAfterMeals: "1",
      fingernailsBreak: "2",
      strongBodyOdour: "1",
      undigestedFoodStool: "1",
      heartburnAcidReflux: "2",
      gasBurpingBloating: "3",
      badBreath: "1",
      feelingHungry: "1",
      feelBetterNotEating: "0",
      feelingOverfull: "2",
      heartburnSpicy: "2",
      digestiveProblemsImprove: "1",
      antacidsBringRelief: "1",
      stomachPainBurning: "1",
    },
    digestiveReactions: {
      increasedPulse: "1",
      sinusCongestion: "2",
      alternatingConstipationDiarrhea: "2",
      bloatingStarches: "3",
      hivesWelts: "1",
      excessGas: "3",
      feelingFullHours: "2",
      allergies: "2",
      glutenSensitivity: "3",
      feelingOverfullMeals: "2",
      cravingsBreadPasta: "3",
      increasingFoodReactions: "1",
      frequentUrination: "1",
      feelZonedOut: "2",
      constipationFiber: "0",
      achesPainsSwelling: "1",
      yeastInfection: "1",
      darkCircles: "3",
      nailFungus: "0",
    },
    immuneHealth: {
      runnyNose: "2",
      swollenLymph: "1",
      coldFlu: "1",
      shingles: "0",
      coldSores: "1",
      herpes: "0",
      poorWoundHealing: "1",
      dryIrritatedItchyEyes: "2",
      mucusThroat: "2",
      sinusEarInfections: "1",
    },
    autoimmuneConditions: {
      lupus: false,
      sjogrens: false,
      raynauds: true,
      rheumatoidArthritis: false,
      multipleSclerosis: false,
      ibd: false,
      vasculitis: false,
      psoriasis: true,
      hashimotos: true,
    },
    premenopausalWomen: {
      perimenopausal: "1",
      nightSweats: "1",
      vaginalItchiness: "0",
      fibrocysticBreasts: "1",
      uterineFibroids: "0",
      facialHairGrowth: "0",
      cycleVaries: "1",
      cycleShort: "0",
      lightBloodFlow: "1",
      heavyBloodFlow: "1",
      irritableDepressed: "2",
      acne: "2",
      hairLoss: "1",
      yeastInfections: "1",
      breastPainSwelling: "2",
      decreasedLibido: "1",
      moodSwings: "2",
      lossOfControlUrine: "0",
      palpitations: "1",
      pms: "3",
      pms_note: "Significant mood swings and bloating before period.",
    },
    premenopausalWomenConditions: {
      onBirthControl: false,
      haveIud: false,
      endometriosis: false,
      breastImplants: false,
      takingHrt: false,
      postPartumDepression: true,
      haveBreastCancer: false,
      pregnantNursing: false,
      hysterectomy: false,
    },
  },
  healthAndMedicalConditions: {
    otherConditions:
      "Seasonal allergies, especially in the spring. Also have some eczema on my elbows.",
  },
  clientStatement: {
    clientNameStatement: "Jane Doe",
    signature: "Jane Doe",
    dateSigned: "2024-07-30",
  },
});

export const fullMockIntakeFormData: IntakeFormData = generateMockIntakeFormData();
